import { ServicePage } from '@/components/ui/page-templates/ServicePage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, TrendingUp, Globe, BarChart3 } from 'lucide-react';

export function GtmPage() {
  const breadcrumbs = [
    { label: 'Services', href: '/services' },
    { label: 'Go-to-Market (GTM)', href: '/services/gtm', current: true },
  ];

  return (
    <ServicePage
      variant="partner"
      title="Go-to-Market (GTM) Services"
      description="Strategic go-to-market and market expansion support to launch and scale your product or service effectively."
      breadcrumbs={breadcrumbs}
      category="Professional Services"
    >
      <div className="space-y-14">
        <section className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Our GTM & Expansion Services</h2>
            <p className="mt-2 max-w-3xl text-muted-foreground">
              We help businesses design and execute go-to-market strategies, enter new markets,
              and scale distribution—from positioning and messaging to channel strategy and launch execution.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card variant="bordered" className="border-primary/20 transition-shadow hover:shadow-md">
              <CardHeader>
                <Target className="h-8 w-8 mb-4 text-primary" />
                <CardTitle>Market Entry & Launch</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  End-to-end support for launching in new segments or geographies.
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>Positioning and messaging frameworks</li>
                  <li>Launch planning and execution</li>
                  <li>Pricing and packaging strategy</li>
                  <li>Channel and partner strategy</li>
                </ul>
              </CardContent>
            </Card>

            <Card variant="bordered" className="border-primary/20 transition-shadow hover:shadow-md">
              <CardHeader>
                <TrendingUp className="h-8 w-8 mb-4 text-primary" />
                <CardTitle>Scale & Expansion</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Grow your reach and revenue with structured expansion playbooks.
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>GTM playbook development</li>
                  <li>Sales and marketing alignment</li>
                  <li>International expansion</li>
                  <li>Product-led growth strategy</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-semibold tracking-tight">How We Work</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card variant="bordered" className="border-primary/10 bg-muted/50">
              <CardContent className="pt-6">
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">Strategy & Planning</h3>
                <p className="text-muted-foreground">
                  We define your target segments, value proposition, and a clear path to market.
                </p>
              </CardContent>
            </Card>
            <Card variant="bordered" className="border-primary/10 bg-muted/50">
              <CardContent className="pt-6">
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">Execution</h3>
                <p className="text-muted-foreground">
                  We support launch and expansion with hands-on execution and measurable milestones.
                </p>
              </CardContent>
            </Card>
            <Card variant="bordered" className="border-primary/10 bg-muted/50">
              <CardContent className="pt-6">
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">Optimization</h3>
                <p className="text-muted-foreground">
                  We iterate with data and feedback to improve conversion and scale.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </ServicePage>
  );
}
